import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UrlReferenceService } from '../../shared/services/url-reference.service';
import { ToastrService } from 'ngx-toastr';
import { CompanyService } from '../../shared/services/company.service';
import { Company } from '../../models/company.model';
import { LoaderComponent } from '../../components/loader/loader.component';

@Component({
  selector: 'app-my-companies',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    LoaderComponent,
  ],
  templateUrl: './my-companies.component.html',
  styleUrls: ['./my-companies.component.scss'],
})
export class MyCompaniesComponent implements OnInit {
  companies: Company[] = [];
  isLoading = true;
  error: string | null = null;
  userId: string = '';
  addEmployee: boolean = false;
  userEmail: string = '';

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private urlRef: UrlReferenceService,
    private companyService: CompanyService,
    private toastr: ToastrService,
    private translateService: TranslateService
  ) {}

  private getUserIdFromLocalStorage(): string {
    const keycloakProfile = localStorage.getItem('keycloakUserProfile');
    if (keycloakProfile) {
      try {
        const profile = JSON.parse(keycloakProfile);
        this.userEmail = profile.email;
        return profile.id;
      } catch (error) {
        console.error('Error parsing Keycloak profile:', error);
        return '';
      }
    }
    return '';
  }

  ngOnInit(): void {
    this.userId = this.getUserIdFromLocalStorage();

    this.route.queryParams.subscribe((params) => {
      this.addEmployee = params['addEmployee'] === 'true';
    });

    this.fetchCompanies();
  }

  fetchCompanies(): void {
    this.isLoading = true;
    this.error = null; // Clear previous error

    console.log('Fetching companies for userId:', this.userId, 'userEmail:', this.userEmail);

    this.companyService
      .getCompaniesByUserIdOrUserEmail(this.userId, this.userEmail)
      .subscribe({
        next: (data) => {
          console.log('Successfully fetched companies:', data);
          console.log('Data type:', typeof data);
          console.log('Is array:', Array.isArray(data));
          console.log('Data length:', data?.length);

          // Ensure data is an array and contains valid company objects
          if (Array.isArray(data)) {
            this.companies = data.filter(company => company && (company.id || company.name));
            console.log('Filtered companies:', this.companies);
          } else if (data && typeof data === 'object' && (data as any).companies) {
            // Handle case where response is wrapped in an object
            const responseData = data as any;
            this.companies = Array.isArray(responseData.companies) ? responseData.companies : [];
          } else {
            console.warn('Unexpected data structure:', data);
            this.companies = [];
          }

          this.isLoading = false;
          this.error = null; // Clear error on success
        },
        error: (err) => {
          console.error('Error fetching companies:', err);
          console.error('Error status:', err.status);
          console.error('Error message:', err.message);

          // Only set error if it's not a temporary network issue
          if (err.status !== 0 && err.status !== 502) {
            this.error = this.translateService.instant(
              'ERRORS.FAILED_LOAD_COMPANIES'
            );
            this.toastr.error(
              this.translateService.instant('ERRORS.FAILED_LOAD_COMPANIES'),
              this.translateService.instant('ERRORS.ERROR')
            );
          } else {
            console.log('Network or gateway error - not showing error to user');
            this.companies = []; // Set empty array so no data state is shown
          }

          this.isLoading = false;
        },
      });
  }

  viewCompanyDetails(companyId: any): void {
    const company = this.companies.find((c) => c.id === companyId);
    if (company && company.id) {
      if (this.addEmployee) {
        this.companyService.setCompanyDetails(company);
        this.companyService.setCompanyDataResponse(company);
        this.router.navigate(['/import-employees']);
      } else {
        this.router.navigate(['/dashboard/companies/form'], {
          queryParams: { companyId: company.id, isNew: false },
        });
      }
    } else {
      this.toastr.error(
        this.translateService.instant('ERRORS.COMPANY_NOT_FOUND')
      );
    }
  }

  editCompanyOnboarding(company: Company): void {
    // Fetch the latest company data to ensure we have the most up-to-date information
    this.companyService
      .getCompanyByCompanyId(company.id!.toString())
      .subscribe({
        next: (latestCompany) => {
          console.log('Latest company data fetched:', latestCompany);
          // Set the latest company data in the service
          this.companyService.setCompanyDetails(latestCompany);

          // Navigate to onboarding with state indicating it's from dashboard
          this.router.navigate(['/onboarding'], {
            state: { fromDashboard: true, selectedCompany: latestCompany },
          });
        },
        error: (err) => {
          console.error('Error fetching latest company data:', err);
          // Fallback to using the existing company data
          this.companyService.setCompanyDetails(company);

          this.router.navigate(['/onboarding'], {
            state: { fromDashboard: true, selectedCompany: company },
          });
        },
      });
  }

  addNewCompany(): void {
    this.router.navigate(['/dashboard/companies/form'], {
      queryParams: { isNew: true },
    });
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}
